import emptyStateTexture from '@/assets/empty-state-texture.svg';
import { ShimmerText } from '@/components/feedback/shimmer-text';
import { TypeWriterText } from '@/components/feedback/typewriter-text';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { close, Sidebar, SidebarHeader, toggle } from '@/features/sidebar';
import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useNetworkStatus } from '@/hooks/browser/use-network-status';
import { useAnalytics } from '@/services/analytics';
import { servicesQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useSupportChat } from '@/features/support';
import { copyToClipboard } from '@/utils/clipboard';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import {
  SUPPORTED_FILE_TYPES,
  type CopilotMessage,
  type CopilotSession,
  type CopilotStatefulMessage,
  type CopilotStatefulToolCall,
  type CopilotUploadFile,
} from '@customerio/hydra-copilot';
import {
  useCopilotAvailableTools,
  useCopilotFileUpload,
  useCopilotMessages,
  useCopilotMeta,
  useCopilotStatus,
} from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Avatar,
  Banner,
  Box,
  Button,
  Divider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateGraphic,
  EmptyStateTitle,
  Form,
  Icon,
  Image,
  LabelIcon,
  Link,
  Paragraph,
  Scrollable,
  Strong,
  Text,
  TextArea,
  Tooltip,
  Truncated,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { motion } from 'framer-motion';
import plur from 'plur';
import type { ComponentProps } from 'react';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';
import * as chatStyles from './chat.css';
import { FileChipList, type FileData } from './components/file-chip';
import { FileUploadArea, type FileUploadAreaRef } from './components/file-upload-area';
import { Markdown } from './components/primitives/markdown';
import { MessageAction, MessageActionsContainer, MessageContainer } from './components/primitives/message-container';
import { SingleImageMessage } from './components/primitives/single-image-message';
import { ToolCall, ToolCallSet } from './components/primitives/tool-call';
import { References } from './components/references';
import { useCopilotChatContext, useCopilotSessionContext } from './components/session-provider';
import type { SuggestionState } from './components/suggestions';
import { Suggestions, useSuggestions } from './components/suggestions';
import { Tour } from './components/tour';
import { groupUserMessages, type ProcessedMessage } from './helpers/group-user-messages';
import { useCopilotHistory } from './hooks';
import { useIsFullScreenAssistantPage } from './index';
import * as styles from './index.css';
import { useCustomerFeedback } from '@/features/customer-feedback';

export function CopilotActions() {
  const session = useCopilotSessionContext();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  const safeNavigate = useSafeNavigate();

  if (workspace == null) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTriggerButton isIconOnly icon="more" variant="subtle" size="sm" />
      <DropdownMenuContent>
        <DropdownMenuItem
          icon="add"
          onClick={() => {
            session.reset();
            track('aiAssistant', 'newChatClicked', {});
          }}
        >
          New Chat
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="time-window"
          onClick={() => {
            toggle('copilot-history');
          }}
        >
          History
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="edit"
          onClick={() => {
            track('aiAssistant', 'editBusinessProfileClicked', {});
            // href doesn't close the dropdown, so we use safeNavigate
            safeNavigate(`/workspaces/${workspace.id}/settings/ai-business-profile`);
          }}
        >
          Edit Business Profile
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CopilotName() {
  const session = useCopilotSessionContext();
  const meta = useCopilotMeta(session);

  return (
    <Truncated>
      <TypeWriterText text={meta.name ?? 'Assistant'} />
    </Truncated>
  );
}

export function CopilotInput() {
  const { session, input, setInput } = useCopilotChatContext();

  return <Input session={session} input={input} setInput={setInput} />;
}

export function CopilotSupportButton() {
  const supportChat = useSupportChat();

  if (!supportChat.isVisible || !supportChat.isHandoverAllowed) {
    return null;
  }

  return (
    <Tooltip content="Chat with support" isCustom={true}>
      {(context) => (
        <Button
          variant="subtle"
          onClick={async () => {
            const success = await supportChat.openChat();
            if (success) {
              close();
            }
          }}
          isLoading={supportChat.isLoading}
          size="sm"
          isIconOnly={true}
          icon="chat-question"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        />
      )}
    </Tooltip>
  );
}

export function CopilotMessages() {
  const session = useCopilotSessionContext();
  const auth = useAuth();
  const messages = useCopilotMessages(session);
  const status = useCopilotStatus(session);
  const isSending = status === 'sending';
  const supportChat = useSupportChat();
  const { track } = useAnalytics();
  const suggestionState = useSuggestions(session);
  const history = useCopilotHistory();

  const hasExistingSessions = (history.data?.pages?.[0]?.body?.meta?.pagination?.total ?? 0) > 0;
  const groupedMessages = useMemo(() => groupUserMessages(messages), [messages]);

  const handleSuggestionClick = async (message: string, index: number) => {
    try {
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  return (
    <Box flexGrow="1" h="0" display="flex" flexDirection="column">
      {status === 'error' ? (
        <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
          <EmptyState>
            <EmptyStateTitle icon="error">Failed to load</EmptyStateTitle>
            <EmptyStateDescription>There was an error loading Assistant. Please try again.</EmptyStateDescription>
            <EmptyStateActions>
              <Button
                variant="primary"
                onClick={() => {
                  session.reset();
                }}
              >
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyState>
        </Box>
      ) : (
        <>
          <ChatContents h="0" flexGrow="1" gap="150" px="150" py="200">
            {messages.length === 0 ? (
              <Box flexGrow="1" display="flex" justifyContent="center" alignItems="center" position="relative">
                <Image
                  src={emptyStateTexture}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    margin: 'auto',
                    opacity: 0.25,
                  }}
                />
                <EmptyState position="relative">
                  <EmptyStateGraphic>
                    <LabelIcon name="chat" color="purple" size="lg" />
                  </EmptyStateGraphic>
                  <EmptyStateTitle>
                    <TimeOfDayGreeting />
                    {auth.user?.first_name != null ? `, ${auth.user.first_name}` : ''}
                  </EmptyStateTitle>
                  <EmptyStateDescription>
                    You can ask me anything about Customer.io
                    {hasExistingSessions ? (
                      <>
                        {' '}
                        or{' '}
                        <Link onClick={() => toggle('copilot-history')} variant="secondary">
                          continue a chat
                        </Link>
                      </>
                    ) : null}
                    .
                  </EmptyStateDescription>
                  <EmptyStateActions>
                    <Box display="flex" flexWrap="wrap" gap="100" justifyContent="center">
                      <Suggestions
                        suggestions={suggestionState.isVisible ? suggestionState.suggestions : []}
                        onSuggestionClick={handleSuggestionClick}
                        shouldShowPlaceholder={true}
                        shouldIncludeHumanSupport={supportChat.isHandoverAllowed}
                      />
                    </Box>
                  </EmptyStateActions>
                </EmptyState>
              </Box>
            ) : (
              <>
                {groupedMessages.map((message, index) => (
                  <Message
                    key={index}
                    message={message}
                    session={session}
                    isLastMessage={!isSending && index === groupedMessages.length - 1}
                  />
                ))}
                {isSending ? (
                  <AnimateMessage>
                    <ShimmerText my="100" px="125">
                      Thinking&hellip;
                    </ShimmerText>
                  </AnimateMessage>
                ) : null}
              </>
            )}
          </ChatContents>
          {messages.length > 0 ? (
            <NextMessageSuggestions
              state={suggestionState}
              onSuggestionClick={handleSuggestionClick}
              shouldIncludeHumanSupport={supportChat.isHandoverAllowed}
            />
          ) : null}
        </>
      )}
    </Box>
  );
}

export function SidebarChat() {
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  const safeNavigate = useSafeNavigate();
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();
  const isFullScreenEnabled = hasFeatureFlag(AccountFeatureFlag.AiCopilotFullscreen);

  if (workspace == null) {
    return null;
  }

  return (
    <Sidebar
      id="copilot"
      onOpenChange={(isOpen) => {
        if (isOpen) {
          track('aiAssistant', 'opened', {});
        } else {
          track('aiAssistant', 'closed', {});
        }
      }}
    >
      <Box display="flex" flexDirection="column" h="full" w="full">
        <SidebarHeader
          title={<CopilotName />}
          leadingActions={<CopilotActions />}
          trailingActions={
            <>
              <CopilotSupportButton />

              {isFullScreenEnabled && !isFullScreenAssistantPage ? (
                <Tooltip content="Open full screen" isCustom={true}>
                  {(context) => (
                    <Button
                      variant="subtle"
                      size="sm"
                      icon="expand"
                      isIconOnly={true}
                      onClick={() => {
                        if (workspace != null) {
                          safeNavigate(`/workspaces/${workspace.id}/assistant`);
                        }
                      }}
                      ref={context.reference.setReference}
                      {...context.reference.getReferenceProps()}
                    />
                  )}
                </Tooltip>
              ) : null}
            </>
          }
          onClose={() => {
            toggle('copilot');
          }}
        />
        <Tour />
        <CopilotMessages />
        <CopilotInput />
      </Box>
    </Sidebar>
  );
}

/**
 * This component keeps the chat scrolled to the bottom if
 * the user is already at the bottom of the chat.
 */
const ChatContents = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode } & Omit<ComponentProps<typeof Scrollable>, 'overflow'>
>(({ children, ...props }, passedRef) => {
  const [isAtBottom, setIsAtBottom] = useState(true);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current == null) {
      return;
    }

    // Use MutationObserver to detect content changes
    const observer = new MutationObserver(() => {
      if (isAtBottom === true && ref.current != null) {
        ref.current.scrollTop = ref.current.scrollHeight;
      }
    });

    // Observe changes to the scrollable element and its descendants
    observer.observe(ref.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [isAtBottom]);

  return (
    <Scrollable
      overflow="y"
      display="flex"
      flexDirection="column"
      gap="100"
      onTouchEdge={(edge) => {
        if (edge === 'bottom') {
          setIsAtBottom(true);
        }
      }}
      onLeaveEdge={(edge) => {
        if (edge === 'bottom') {
          setIsAtBottom(false);
        }
      }}
      ref={mergeRefs([ref, passedRef])}
      {...props}
    >
      {children}
    </Scrollable>
  );
});

function Message({
  message,
  session,
  isLastMessage = false,
}: {
  message: ProcessedMessage;
  session: CopilotSession;
  isLastMessage?: boolean;
}) {
  const auth = useAuth();
  const snackbars = useSnackbars();

  const submitFeedbackMutation = servicesQuery.recordCopilotFeedback.useMutation({
    onSuccess: (_, variables) => {
      snackbars.create({
        message: 'Thank you for your feedback!',
      });

      if (!('meta' in message) || message.meta == null || (message.meta as any).id == null) {
        return;
      }

      session.setMessageMeta((message.meta as any).id, {
        ...message.meta,
        thumbs_up: variables.body?.thumbs_up,
        thumbs_down: variables.body?.thumbs_down,
      });
    },
    onError: (error) => {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Failed to submit feedback. Please try again.',
        variant: 'error',
      });
    },
  });

  const fileChips = useMemo(() => {
    if (message.role !== 'user' || message.type !== 'grouped') return [];

    return message.contentMessages.map((contentMsg: CopilotStatefulMessage, index: number) => {
      const displayName = 'display_name' in contentMsg ? contentMsg.display_name ?? 'File' : 'File';
      const content = 'content' in contentMsg ? contentMsg.content : '';

      return {
        id: `readonly-${index}`,
        name: displayName,
        mimeType:
          'mime_type' in contentMsg ? contentMsg.mime_type ?? 'application/octet-stream' : 'application/octet-stream',
        source: content,
      } as FileData;
    });
  }, [message]);

  // // TODO: show full size file in a modal
  // const handleShowFile = useCallback(
  //   (fileId: string) => {
  //     const file = fileChips.find((f: { id: string; source: string }) => f.id === fileId);
  //     if (file != null && file.source !== '') {
  //       window.open(file.source, '_blank');
  //     }
  //   },
  //   [fileChips],
  // );

  if (message.role === 'user') {
    let content: React.ReactNode = null;

    if (message.type === 'grouped') {
      // Grouped message: text + files
      content = (
        <>
          {fileChips.length > 0 ? <FileChipList files={fileChips} readOnly={true} session={session} /> : null}
          {!isEmptyString(message.text) ? <Markdown>{message.text}</Markdown> : null}
        </>
      );
    } else if (message.type === 'text') {
      // Single text message
      content = <Markdown>{message.text}</Markdown>;
    } else if (message.type === 'content' && 'content' in message) {
      // Single content message - use SingleImageMessage to handle blob conversion
      content = (
        <SingleImageMessage
          source={message.content}
          alt={message.display_name ?? 'File'}
          readOnly={true}
          session={session}
        />
      );
    }

    return (
      <MessageContainer variant="user">
        <Avatar backgroundColor="invert" color="invert" name={auth.user.name} />
        <Box mt="025" display="flex" flexDirection="column" gap="100">
          {content}
        </Box>
      </MessageContainer>
    );
  } else if (message.role === 'model' && message.type === 'text') {
    const sources = 'meta' in message && message.meta?.sources != null ? message.meta.sources : [];
    const hasMetaId = 'meta' in message && message.meta?.id != null;
    const hasThumbsUp = 'meta' in message && message.meta?.thumbs_up === true;
    const hasThumbsDown = 'meta' in message && message.meta?.thumbs_down === true;
    const isThought = 'thought' in message && message.thought === true;

    if (isThought) {
      return <ThoughtMessage message={message} />;
    }

    return (
      <AnimateMessage>
        <MessageContainer variant="model">
          <Markdown>{message.text}</Markdown>
          {sources.length > 0 ? <References references={sources} /> : null}
          {hasMetaId ? (
            <Box>
              <MessageActionsContainer isVisible={isLastMessage}>
                <MessageAction
                  tooltip="Copy to clipboard"
                  onClick={async () => {
                    const success = await copyToClipboard(message.text);
                    if (success) {
                      snackbars.create({
                        message: 'Copied to clipboard',
                      });
                    }
                  }}
                  aria-label="Copy to clipboard"
                >
                  <Icon name="copy-to-clipboard" size="xs" />
                </MessageAction>
                {!hasThumbsDown ? (
                  <MessageAction
                    tooltip="This was helpful"
                    variant={hasThumbsUp ? 'active' : 'default'}
                    onClick={
                      hasThumbsUp
                        ? undefined
                        : () => {
                            submitFeedbackMutation.mutate({
                              params: {
                                accountId: auth.account.id,
                                sessionId: Number(session.id),
                                logId: message.meta.id,
                              },
                              body: {
                                thumbs_up: true,
                              },
                            });
                          }
                    }
                    aria-label="Thumbs up"
                  >
                    <Icon name="thumbs-up" size="xs" />
                  </MessageAction>
                ) : null}
                {!hasThumbsUp ? (
                  <MessageAction
                    tooltip="This was not helpful"
                    variant={hasThumbsDown ? 'active' : 'default'}
                    onClick={
                      hasThumbsDown
                        ? undefined
                        : () => {
                            submitFeedbackMutation.mutate({
                              params: {
                                accountId: auth.account.id,
                                sessionId: Number(session.id),
                                logId: message.meta.id,
                              },
                              body: {
                                thumbs_down: true,
                              },
                            });
                          }
                    }
                    aria-label="Thumbs down"
                  >
                    <Icon name="thumbs-down" size="xs" />
                  </MessageAction>
                ) : null}
              </MessageActionsContainer>
            </Box>
          ) : null}
        </MessageContainer>
      </AnimateMessage>
    );
  } else if (message.role === 'model' && message.type === 'tool_call') {
    return <ToolCalls message={message} session={session} />;
  } else if (message.role === 'model' && message.type === 'error') {
    return (
      <AnimateMessage>
        <Banner variant="error" title="Error">
          {message.error}
        </Banner>
      </AnimateMessage>
    );
  } else {
    // typescript hack to ensure all cases are handled
    const never: never = message;
    // eslint-disable-next-line no-console
    console.error('Unknown message type', never);
    return null;
  }
}

function ThoughtMessage({ message }: { message: CopilotStatefulMessage & { type: 'text'; thought?: boolean } }) {
  const [isOpen, setIsOpen] = useState(false);

  const durationSeconds = 'duration' in message && typeof message.duration === 'number' ? message.duration : null;

  const formattedDuration = (() => {
    if (durationSeconds == null) return null;

    if (durationSeconds < 60) {
      return `${durationSeconds}s`;
    } else {
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = durationSeconds % 60;
      return `${minutes}m ${seconds}s`;
    }
  })();

  return (
    <AnimateMessage>
      <Box
        display="flex"
        flexDirection="column"
        overflow="hidden"
        as="details"
        open={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
      >
        <Box as="summary" display="flex" alignItems="center" gap="050" py="075" cursor="pointer">
          {isOpen ? <Icon name="chevron-down" size="sm" /> : <Icon name="chevron-right" size="sm" />}
          <Text
            as={formattedDuration == null ? ShimmerText : 'span'}
            color={formattedDuration == null ? 'base' : 'subtle'}
          >
            {formattedDuration != null ? `Thought for ${formattedDuration}` : 'Thinking\u2026'}
          </Text>
        </Box>
        {isOpen ? (
          <ToolCallSet>
            <Markdown>{message.text}</Markdown>
          </ToolCallSet>
        ) : null}
      </Box>
    </AnimateMessage>
  );
}

function ToolCalls({
  message,
  session,
}: {
  message: CopilotStatefulMessage & { type: 'tool_call' };
  session: CopilotSession;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const tools = useCopilotAvailableTools(session);
  const toolsWithCustomRendering = message.tool_calls.filter((toolCall) => tools[toolCall.name]?.render != null);
  const toolsWithoutCustomRendering = message.tool_calls.filter((toolCall) => tools[toolCall.name]?.render == null);

  const status = combinedStatus(toolsWithoutCustomRendering);
  const toolCount = toolsWithoutCustomRendering.length;
  const failedCount = toolsWithoutCustomRendering.filter((toolCall) => toolCall.status === 'error').length;

  // Check if any tool has summary to use custom text
  const toolsWithSummary = toolsWithoutCustomRendering.filter((toolCall) => toolCall.summary != null);
  const hasCustomDisplayText = toolsWithSummary.length > 0;

  let label = '';
  if (status === 'executing') {
    if (hasCustomDisplayText && toolsWithSummary.length === 1 && toolCount === 1) {
      // Use custom "executing" text for single tool with non-empty summary
      const executingText = trimPunctuation(toolsWithSummary[0].summary?.executing);
      // Fallback if executing text is empty
      label = executingText.length > 0 ? executingText + '…' : `Calling ${toolCount} ${plur('tool', toolCount)}`;
    } else {
      // Standard text for multiple tools or empty summary
      label = `Calling ${toolCount} ${plur('tool', toolCount)}`;
    }
  } else if (status === 'error') {
    label =
      failedCount === toolCount
        ? `${toolCount} ${plur('tool', toolCount)} failed`
        : `Called ${toolCount} ${plur('tool', toolCount)}, ${failedCount} failed`;
  } else {
    if (hasCustomDisplayText && toolsWithSummary.length === 1 && toolCount === 1) {
      // Use custom "success" text for single tool with non-empty summary
      const successText = trimPunctuation(toolsWithSummary[0].summary?.success);
      // Fallback if success text is empty
      label = successText.length > 0 ? successText : `Called ${toolCount} ${plur('tool', toolCount)}`;
    } else {
      // Standard text for multiple tools or empty summary
      label = `Called ${toolCount} ${plur('tool', toolCount)}`;
    }
  }

  return (
    <>
      {toolsWithCustomRendering.length > 0 ? (
        <>
          {toolsWithCustomRendering.map((toolCall, index) => {
            const render = tools[toolCall.name]?.render;

            // we have already checked that the render function exists
            // but this is to narrow the type
            if (render == null) {
              return (
                <AnimateMessage key={index}>
                  <ToolCallSet>
                    <ToolCall
                      status={'error'}
                      name={toolCall.name}
                      error={'Something went wrong, we are missing the render function for this tool.'}
                    />
                  </ToolCallSet>
                </AnimateMessage>
              );
            }

            return (
              <AnimateMessage key={index}>
                <Box>{render({ ...toolCall, session })}</Box>
              </AnimateMessage>
            );
          })}
        </>
      ) : null}
      {toolsWithoutCustomRendering.length > 0 ? (
        <AnimateMessage>
          <Box
            display="flex"
            flexDirection="column"
            overflow="hidden"
            as="details"
            open={isOpen}
            onToggle={() => setIsOpen(!isOpen)}
          >
            <Box as="summary" display="flex" alignItems="center" gap="050" py="075" cursor="pointer">
              {isOpen ? <Icon name="chevron-down" size="sm" /> : <Icon name="chevron-right" size="sm" />}
              <Text
                as={status === 'executing' ? ShimmerText : 'span'}
                color={status === 'executing' ? 'base' : 'subtle'}
              >
                {label}
              </Text>
            </Box>
            <ToolCallSet>
              {toolsWithoutCustomRendering.map((toolCall, index) => (
                <ToolCall
                  key={index}
                  status={toolCall.status}
                  name={toolCall.name}
                  error={toolCall?.response?.error}
                  summary={toolCount > 1 ? toolCall.summary : undefined}
                />
              ))}
            </ToolCallSet>
          </Box>
        </AnimateMessage>
      ) : null}
    </>
  );
}

function combinedStatus(toolCalls: CopilotStatefulToolCall[]) {
  const statuses = toolCalls.map((toolCall) => toolCall.status);
  if (statuses.includes('executing')) {
    return 'executing';
  } else if (statuses.includes('error')) {
    return 'error';
  } else if (statuses.includes('success')) {
    return 'success';
  }

  return 'success';
}

function NextMessageSuggestions({
  state,
  onSuggestionClick,
  shouldIncludeHumanSupport = false,
}: {
  state: SuggestionState;
  onSuggestionClick: (message: string, index: number) => void;
  shouldIncludeHumanSupport?: boolean;
}) {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [suggestionsHidden, setSuggestionsHidden] = useLocalStorage('copilot-suggestions-hidden', false);
  const { track } = useAnalytics();

  if (state.isVisible === false || state.isLoading === true) {
    return null;
  }

  return (
    <Box>
      <Divider />
      <Box display="flex" alignItems="center" justifyContent="space-between" px="150" pt="100">
        <Text size="xs">
          <Link
            icon={suggestionsHidden ? 'view-on' : 'view-off'}
            isUnderlined={false}
            variant="secondary"
            onClick={() => {
              setSuggestionsHidden((v) => {
                const newValue = !v;
                track('aiAssistant', newValue ? 'suggestionsHidden' : 'suggestionsShown', {});
                return newValue;
              });
            }}
          >
            {suggestionsHidden ? 'Show suggestions' : 'Hide suggestions'}
          </Link>
        </Text>
        {suggestionsHidden ? null : (
          <Box>
            <Button
              isIconOnly={true}
              icon="chevron-left"
              variant="subtle"
              size="sm"
              onClick={() => {
                if (scrollableRef.current != null) {
                  scrollableRef.current.scrollTo({
                    left: scrollableRef.current.scrollLeft - scrollableRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
            />
            <Button
              isIconOnly={true}
              icon="chevron-right"
              variant="subtle"
              size="sm"
              onClick={() => {
                if (scrollableRef.current != null) {
                  scrollableRef.current.scrollTo({
                    left: scrollableRef.current.scrollLeft + scrollableRef.current.clientWidth,
                    behavior: 'smooth',
                  });
                }
              }}
            />
          </Box>
        )}
      </Box>
      {suggestionsHidden ? null : (
        <Scrollable
          overflow="x"
          display="flex"
          gap="100"
          ref={scrollableRef}
          p="100"
          className={chatStyles.hiddenScrollbar}
        >
          <Suggestions
            suggestions={state.suggestions}
            onSuggestionClick={onSuggestionClick}
            shouldShowPlaceholder={false}
            shouldIncludeHumanSupport={shouldIncludeHumanSupport}
          />
        </Scrollable>
      )}
    </Box>
  );
}

function Input({
  session,
  input,
  setInput,
}: {
  input: string;
  setInput: (value: string) => void;
  session: CopilotSession;
}) {
  const lastInput = useRef<string | null>(null);
  const lastFiles = useRef<CopilotUploadFile[]>([]);
  const fileUploadAreaRef = useRef<FileUploadAreaRef>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const status = useCopilotStatus(session);
  const isLoading = status === 'sending' || status === 'receiving';
  const networkStatus = useNetworkStatus();

  // File upload functionality
  const fileUpload = useCopilotFileUpload(session);

  // Handle files from both drag drop and file input
  const handleFiles = useCallback(
    (files: File[]) => {
      const result = fileUpload.addFiles(files);

      const messages: string[] = [];

      if (result.errors.length > 0) {
        messages.push(...result.errors.map((e) => `${e.name}: ${e.error}`));
      }

      if (messages.length > 0) {
        setUploadError(messages.join('\n'));
      }

      // Immediately start uploading the files
      if (result.addedCount > 0) {
        fileUpload
          .uploadAllFiles()
          .then((uploadResult) => {
            // Handle failed uploads - accumulate with validation errors
            if (uploadResult.failed.length > 0) {
              const failedMessages = uploadResult.failed.map((f) => `${f.name}: ${f.error}`);

              // Append to existing validation errors if any
              setUploadError((prevError) => {
                if (!isEmptyString(prevError)) {
                  return prevError + '\n' + failedMessages.join('\n');
                }
                return failedMessages.join('\n');
              });

              // Report to Sentry
              for (const f of uploadResult.failed) {
                sentryCaptureException(new Error(`File upload failed for ${f.name}: ${f.error}`));
              }
            } else if (uploadResult.successful.length > 0 && messages.length === 0) {
              // Clear error if all uploads succeeded and there were no validation errors
              setUploadError(null);
            }
          })
          .catch((error) => {
            // This catch should rarely be hit since uploadAllFiles uses Promise.allSettled
            sentryCaptureException(error);
            setUploadError(error instanceof Error ? error.message : 'Failed to upload files');
          });
      }
    },
    [fileUpload],
  );

  const fileChips = useMemo(
    () =>
      fileUpload.files.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        mimeType: file.mimeType,
        source: file.file,
        status: file.status,
      })),
    [fileUpload.files],
  );

  async function submit(input: string, files: CopilotUploadFile[] = []) {
    setError(null);
    try {
      // Create message payload
      const messages: CopilotMessage[] = [];

      // Add file messages for uploaded files
      const uploadedFiles = files.filter((f) => f.status === 'uploaded' && f.uploadedUrl != null);
      for (const file of uploadedFiles) {
        messages.push({
          role: 'user',
          type: 'content',
          content: file.uploadedUrl ?? '',
          mime_type: file.mimeType,
          display_name: file.name,
        });
      }

      // Add text message if there's input
      if (!isEmptyString(input)) {
        messages.push({
          role: 'user',
          type: 'text',
          text: input,
        });
      }

      // Clear input and files indicating that the user has sent the message
      setInput('');
      fileUpload.clearFiles();

      // If we have messages to send, send them
      if (messages.length > 0) {
        await session.sendMessage(messages);
      }
    } catch (error) {
      sentryCaptureException(error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  }

  const handleUploadButtonClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileUploadAreaRef.current != null) {
      fileUploadAreaRef.current.triggerFileInput();
    }
  }, []);

  const hasContent = useMemo<boolean>(
    () => !isEmptyString(input) || fileUpload.files.length > 0,
    [input, fileUpload.files],
  );
  const hasUploadingFiles = useMemo<boolean>(
    () => fileUpload.files.some((f) => f.status === 'uploading' || f.status === 'pending'),
    [fileUpload.files],
  );
  const canSubmit = hasContent && !hasUploadingFiles;

  return (
    <Box p="100">
      {uploadError != null ? (
        <Banner
          title="Upload Error"
          variant="error"
          mb="100"
          shouldShowDismissButton={true}
          onClose={() => setUploadError(null)}
        >
          <Box style={{ whiteSpace: 'pre-line' }}>{uploadError}</Box>
        </Banner>
      ) : null}
      {error != null ? (
        <Banner
          title="Error"
          variant="error"
          mb="100"
          action={{
            label: 'Retry',
            onClick: () => {
              if (lastInput.current != null || lastFiles.current.length > 0) {
                submit(lastInput.current ?? '', lastFiles.current);
              }
            },
          }}
          shouldShowDismissButton={true}
          onClose={() => setError(null)}
        >
          {error}
        </Banner>
      ) : null}

      <FileUploadArea
        ref={fileUploadAreaRef}
        onFilesSelected={handleFiles}
        disabled={isLoading || networkStatus === 'offline'}
        style={{ position: 'relative' }}
        acceptedMimeTypes={Object.keys(SUPPORTED_FILE_TYPES)}
      >
        <Form
          onSubmit={async (e) => {
            e.preventDefault();

            if (!canSubmit) {
              return;
            }

            if (networkStatus === 'offline') {
              return;
            }

            if (isLoading) {
              return;
            }

            lastInput.current = input;
            lastFiles.current = Array.from(fileUpload.files);
            await submit(input, Array.from(fileUpload.files));
          }}
          position="relative"
          autoLoading={false}
        >
          <TextArea
            value={input}
            onInput={(e) => setInput(e.target.value)}
            onPaste={(e) => {
              // Extract image files from clipboard
              const items = Array.from(e.clipboardData?.items ?? []);
              const imageFiles = items
                .filter((item) => item.type.startsWith('image/'))
                .map((item) => item.getAsFile())
                .filter((file): file is File => file !== null);

              if (imageFiles.length > 0) {
                e.preventDefault();
                handleFiles(imageFiles);
              }
            }}
            placeholder={fileChips.length > 0 ? 'Add a message (optional)' : 'How can I help you today?'}
            shouldSubmitOnEnter={true}
            ariaLabel="Send message to Assistant"
            resize="none"
            minRows={1}
            maxRows={6}
            unsafe_textareaWrapperClassName={
              fileChips.length > 0
                ? `${styles.chatTextareaWrapper} ${styles.chatTextareaWrapperWithFiles}`
                : styles.chatTextareaWrapper
            }
            unsafe_inputClassName={styles.chatTextareaInput}
          />

          {/* File chips positioned absolutely */}
          {fileChips.length > 0 ? (
            <Box
              style={{
                position: 'absolute',
                top: themeVars.space['100'],
                left: themeVars.space['100'],
                right: themeVars.space['600'],
                zIndex: 1,
              }}
            >
              <FileChipList files={fileChips} readOnly={false} onRemove={fileUpload.removeFile} session={session} />
            </Box>
          ) : null}

          <Button
            icon="upload"
            isIconOnly={true}
            variant="tertiary"
            type="button"
            size="sm"
            style={{
              position: 'absolute',
              bottom: themeVars.space['100'],
              right: themeVars.space['600'],
              border: 'none',
            }}
            isDisabled={networkStatus === 'offline' || isLoading}
            onClick={handleUploadButtonClick}
          />
          <Button
            icon="chevron-up"
            isIconOnly={true}
            variant="primary"
            type="submit"
            size="sm"
            style={{
              position: 'absolute',
              bottom: themeVars.space['100'],
              right: themeVars.space['100'],
            }}
            isLoading={isLoading}
            isDisabled={networkStatus === 'offline' || !canSubmit}
          />
        </Form>
      </FileUploadArea>
      <FooterStatment input={input} />
    </Box>
  );
}

function FooterStatment({ input }: { input: string }) {
  const networkStatus = useNetworkStatus();
  const showFeedbackModal = useCustomerFeedback();
  const { track } = useAnalytics();

  return (
    <Paragraph text="product-pxs-secondary" pt="050" color="subtle">
      {networkStatus === 'offline' ? (
        <>{input.length > 0 ? <>You can't send messages when offline</> : <>You are currently offline</>}</>
      ) : input.length > 0 ? (
        <>
          <Strong weight="medium">Shift + Return</Strong> to add a new line
        </>
      ) : (
        <>
          AI got something wrong?{' '}
          <Link
            variant="secondary"
            onClick={() => {
              track('aiAssistant', 'feedbackLinkClicked', {});
              void showFeedbackModal({
                feature: 'AI Assistant',
                onSubmit: (values) => {
                  track('aiAssistant', 'feedbackFormSubmitted', {
                    Feedback: values.feedback,
                  });
                },
              });
            }}
          >
            Give feedback
          </Link>
        </>
      )}
    </Paragraph>
  );
}

function AnimateMessage({ children }: { children: React.ReactNode }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
      {children}
    </motion.div>
  );
}

function TimeOfDayGreeting() {
  const date = new Date();
  const hours = date.getHours();

  if (hours < 12) {
    return 'Good morning';
  } else if (hours < 18) {
    return 'Good afternoon';
  } else {
    return 'Good evening';
  }
}

function trimPunctuation(text?: string) {
  if (text == null || isEmptyString(text)) {
    return '';
  }

  return text.trim().replace(/\.+$/, '');
}
